import React, { useState, useEffect } from 'react';
import { Tree } from 'antd';
import type { DataNode, TreeProps } from 'antd/es/tree';
import { tv } from 'tailwind-variants';
import { DotsSixVertical } from '@phosphor-icons/react';
const treeVariants = tv({
  base: 'bg-white flex-center border rounded-2xl px-2 py-1 gap-2',
  variants: {
    size: {
      sm: 'text-sm h-[64px]',
      md: 'text-base',
      lg: 'text-lg',
    },
    variant: {
      default: '',
      bordered: 'border border-gray-300',
      shadow: 'shadow-md',
      disable: 'bg-[var(--bgBlackOpacity)] text-gray-400 cursor-not-allowed',
    },
  },
  defaultVariants: {
    size: 'sm',
    variant: 'default',
  },
});

type TreeComponentProps = {
  children: React.ReactNode;
  className?: string;
  variantStatus?: 'default' | 'bordered' | 'shadow' | 'disable';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  radius?: 'sm' | 'md' | 'lg';
};

const TreeComponent: React.FC<TreeComponentProps> = ({ children, className, variantStatus = 'default', size }) => {
  const [treeData, setTreeData] = useState<DataNode[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const generateAllKeys = (nodes: DataNode[]): string[] => {
    const keys: string[] = [];
    const traverse = (nodeList: DataNode[]) => {
      nodeList.forEach((node) => {
        keys.push(node.key as string);
        if (node.children) traverse(node.children);
      });
    };
    traverse(nodes);
    return keys;
  };

  useEffect(() => {
    const treeClass = `${treeVariants({ variant: variantStatus, size })} ${className || ''}`;

    const nodes = React.Children.toArray(children)
      .map((child) => {
        if (!React.isValidElement(child)) return null;

        type ChildProps = { order: string | number; onDelete?: () => void };
        const childWithProps = React.cloneElement(child as React.ReactElement<ChildProps>, {
          onDelete: () => handleDelete((child as React.ReactElement<ChildProps>).props.order),
        });

        return {
          title: (
            <div className={treeClass}>
              <div className={`${variantStatus === 'disable' && 'hidden'}`}>
                <DotsSixVertical size={20} weight='bold' />
              </div>
              {childWithProps}
            </div>
          ),
          key: String((child as React.ReactElement<ChildProps>).props.order),
        };
      })
      .filter(Boolean) as DataNode[];

    setTreeData(nodes);
    setExpandedKeys(generateAllKeys(nodes));
  }, [children, variantStatus, size, className]);

  const handleDelete = (orderToDelete: string | number) => {
    console.log('orderToDelete', orderToDelete);
    setTreeData((prevData) => prevData.filter((node) => node.key !== String(orderToDelete)));
  };

  const onDrop: TreeProps['onDrop'] = (info) => {
    const dropKey = info.node.key as string;
    const dragKey = info.dragNode.key as string;
    const dropPosition = info.dropPosition;

    const data = [...treeData];
    let dragObj: DataNode;

    // Delete the node dragged out of its old position
    const removeNode = (nodes: DataNode[], key: string): DataNode[] => {
      return nodes.reduce<DataNode[]>((result, node) => {
        if (node.key === key) {
          dragObj = node;
          return result;
        }
        return [...result, node];
      }, []);
    };

    const newData = removeNode(data, dragKey);

    // Locate drop
    const index = newData.findIndex((node) => node.key === dropKey);
    if (index === -1 || dragKey === dropKey) return;

    // Add new node
    const insertIndex = dropPosition < 0 ? index : index + 1;
    newData.splice(insertIndex, 0, dragObj!);

    setTreeData(newData);
    setExpandedKeys(generateAllKeys(newData));
  };

  return (
    <div className=''>
      <Tree
        draggable={variantStatus !== 'disable' ? { icon: false } : false}
        className='[&_.ant-tree-switcher]:hidden [&_.ant-tree-node-content-wrapper]:p-0 [&_.ant-tree-node-content-wrapper]:rounded-2xl mt-3 '
        blockNode
        treeData={treeData}
        onDrop={onDrop}
        expandedKeys={expandedKeys}
        switcherIcon={() => null}
      />
    </div>
  );
};

export default TreeComponent;
